import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Tab,
  Tabs,
  Paper,
  Snackbar,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  getAllTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  toggleTemplate,
  sendMessage,
  broadcastMessage,
  getMessageStats,
  getSentMessages,
  searchUsers,
} from "../../../context/SystemMessage";
import TabPanel from "./TabPanel";
import TemplatesTab from "./TemplatesTab";
import SentMessagesTab from "./SentMessagesTab";
import StatisticsTab from "./StatisticsTab";
import TemplateDialog from "./TemplateDialog";
import ViewTemplateDialog from "./ViewTemplateDialog";
import SendMessageDialog from "./SendMessageDialog";

export default function SystemMessagesComponent() {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [sentMessages, setSentMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Message filters
  const [messageFilters, setMessageFilters] = useState({
    type: "",
    category: "",
    severity: "",
    isRead: "",
    search: "",
  });

  // Template Dialog States
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: "",
    type: "info",
    category: "general",
    title: "",
    message: "",
    severity: "medium",
    variables: [],
    defaultMetadata: {},
    isActive: true,
  });

  // Send Message Dialog States
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendForm, setSendForm] = useState({
    recipientIds: [],
    userSearchQuery: "",
    templateId: "",
    customTitle: "",
    customMessage: "",
    type: "info",
    category: "general",
    severity: "medium",
    isBroadcast: false,
  });
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchInput, setUserSearchInput] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);

  // View Template Dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewTemplate, setViewTemplate] = useState(null);

  useEffect(() => {
    if (tabValue === 0) fetchTemplates();
    if (tabValue === 1) fetchSentMessages();
    if (tabValue === 2) fetchStats();
  }, [tabValue]);

  // Fetch messages when filters change
  useEffect(() => {
    if (tabValue === 1) {
      const delayDebounce = setTimeout(() => {
        fetchSentMessages();
      }, 400);
      return () => clearTimeout(delayDebounce);
    }
  }, [messageFilters]);

  // Debounced user search
  useEffect(() => {
    if (!sendDialogOpen) return;

    const delayDebounce = setTimeout(() => {
      if (userSearchInput.length >= 2) {
        handleUserSearch(userSearchInput);
      } else {
        setUserSearchResults([]);
      }
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [userSearchInput, sendDialogOpen]);

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  // =============== TEMPLATE OPERATIONS ===============
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const data = await getAllTemplates();
      setTemplates(data.templates || []);
    } catch (error) {
      showSnackbar(error.message || "Failed to fetch templates", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTemplateDialog = (template = null) => {
    if (template) {
      setCurrentTemplate(template);
      setTemplateForm({
        name: template.name,
        type: template.type,
        category: template.category,
        title: template.title,
        message: template.message,
        severity: template.severity || "medium",
        variables: template.variables || [],
        defaultMetadata: template.defaultMetadata || {},
        isActive: template.isActive,
      });
    } else {
      setCurrentTemplate(null);
      setTemplateForm({
        name: "",
        type: "info",
        category: "general",
        title: "",
        message: "",
        severity: "medium",
        variables: [],
        defaultMetadata: {},
        isActive: true,
      });
    }
    setTemplateDialogOpen(true);
  };

  const handleCloseTemplateDialog = () => {
    setTemplateDialogOpen(false);
    setCurrentTemplate(null);
  };

  const handleSaveTemplate = async () => {
    try {
      if (currentTemplate) {
        await updateTemplate(currentTemplate._id, templateForm);
        showSnackbar("Template updated successfully", "success");
      } else {
        await createTemplate(templateForm);
        showSnackbar("Template created successfully", "success");
      }
      handleCloseTemplateDialog();
      fetchTemplates();
    } catch (error) {
      showSnackbar(error.message || "Failed to save template", "error");
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm("Are you sure you want to delete this template?"))
      return;

    try {
      await deleteTemplate(templateId);
      showSnackbar("Template deleted successfully", "success");
      fetchTemplates();
    } catch (error) {
      showSnackbar(error.message || "Failed to delete template", "error");
    }
  };

  const handleToggleTemplate = async (templateId, currentStatus) => {
    try {
      await toggleTemplate(templateId);
      showSnackbar(
        `Template ${currentStatus ? "deactivated" : "activated"} successfully`,
        "success"
      );
      fetchTemplates();
    } catch (error) {
      showSnackbar(error.message || "Failed to toggle template", "error");
    }
  };

  const handleViewTemplate = (template) => {
    setViewTemplate(template);
    setViewDialogOpen(true);
  };

  // =============== SEND MESSAGE OPERATIONS ===============
  const handleOpenSendDialog = () => {
    setSendForm({
      recipientIds: [],
      userSearchQuery: "",
      templateId: "",
      customTitle: "",
      customMessage: "",
      type: "info",
      category: "general",
      severity: "medium",
      isBroadcast: false,
    });
    setUserSearchInput("");
    setUserSearchResults([]);
    setSelectedUsers([]);
    setSendDialogOpen(true);
  };

  const handleUserSearch = async (query) => {
    try {
      const users = await searchUsers(query);
      setUserSearchResults(users || []);
    } catch (error) {
      console.error("User search failed:", error);
      setUserSearchResults([]);
    }
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find((t) => t._id === templateId);
    if (template) {
      setSendForm((prev) => ({
        ...prev,
        templateId,
        customTitle: template.title,
        customMessage: template.message,
        type: template.type,
        category: template.category,
        severity: template.severity,
      }));
    }
  };

  const handleSendMessage = async () => {
    try {
      const payload = {};

      // Check if broadcasting
      if (sendForm.isBroadcast) {
        if (sendForm.templateId) {
          payload.templateId = sendForm.templateId;
        } 
        if (!sendForm.customTitle || !sendForm.customMessage) {
          showSnackbar("Please provide message title and content", "warning");
          return;
        }
        payload.customMessage = {
          type: sendForm.type,
          category: sendForm.category,
          title: sendForm.customTitle,
          message: sendForm.customMessage,
          severity: sendForm.severity,
        };
        
        console.log("Broadcasting message with payload:", payload);
        await broadcastMessage(payload);
        showSnackbar(
          "Message broadcasted successfully to all users",
          "success"
        );
      } else {
        // Regular send to specific users
        if (sendForm.recipientIds.length === 1) {
          payload.userId = sendForm.recipientIds[0];
        } else if (sendForm.recipientIds.length > 1) {
          payload.userIds = sendForm.recipientIds;
        } else {
          showSnackbar("Please select at least one recipient", "warning");
          return;
        }

        if (sendForm.templateId) {
          payload.templateId = sendForm.templateId;
        } else {
          if (!sendForm.customTitle || !sendForm.customMessage) {
            showSnackbar("Please provide message title and content", "warning");
            return;
          }

          payload.customMessage = {
            type: sendForm.type,
            category: sendForm.category,
            title: sendForm.customTitle,
            message: sendForm.customMessage,
            severity: sendForm.severity,
          };
        }

        await sendMessage(payload);
        showSnackbar("Message sent successfully", "success");
      }

      setSendDialogOpen(false);

      // Refresh data
      fetchSentMessages();
      if (tabValue === 2) fetchStats();
    } catch (error) {
      showSnackbar(error.message || "Failed to send message", "error");
    }
  };

  // =============== STATS OPERATIONS ===============
  const fetchStats = async () => {
    setLoading(true);
    try {
      const data = await getMessageStats();
      setStats(data);
    } catch (error) {
      showSnackbar(error.message || "Failed to fetch statistics", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchSentMessages = async () => {
    setLoading(true);
    try {
      const filters = {
        limit: 100,
        ...(messageFilters.type && { type: messageFilters.type }),
        ...(messageFilters.category && { category: messageFilters.category }),
        ...(messageFilters.severity && { severity: messageFilters.severity }),
        ...(messageFilters.isRead !== "" && {
          isRead: messageFilters.isRead === "true",
        }),
        ...(messageFilters.search && { search: messageFilters.search }),
      };
      const data = await getSentMessages(filters);
      setSentMessages(data.messages || []);
    } catch (error) {
      showSnackbar(error.message || "Failed to fetch sent messages", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 4,
        px: { xs: 2, sm: 3 },
        backgroundColor: theme.palette.background.default,
        minHeight: "100vh",
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        mb={4}
        color={theme.palette.text.primary}
      >
        System Messages Management
      </Typography>

      <Paper
        sx={{
          width: "100%",
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              color: theme.palette.text.secondary,
            },
            "& .Mui-selected": {
              color: theme.palette.primary.main,
            },
          }}
        >
          <Tab label="Templates" />
          <Tab label="Send Message" />
          <Tab label="Statistics" />
        </Tabs>

        {/* Templates Tab */}
        <TabPanel value={tabValue} index={0}>
          <TemplatesTab
            templates={templates}
            loading={loading}
            onOpenDialog={handleOpenTemplateDialog}
            onView={handleViewTemplate}
            onToggle={handleToggleTemplate}
            onDelete={handleDeleteTemplate}
          />
        </TabPanel>

        {/* Send Message Tab */}
        <TabPanel value={tabValue} index={1}>
          <SentMessagesTab
            sentMessages={sentMessages}
            loading={loading}
            messageFilters={messageFilters}
            setMessageFilters={setMessageFilters}
            onOpenSendDialog={handleOpenSendDialog}
          />
        </TabPanel>

        {/* Statistics Tab */}
        <TabPanel value={tabValue} index={2}>
          <StatisticsTab stats={stats} loading={loading} />
        </TabPanel>
      </Paper>

      {/* Dialogs */}
      <TemplateDialog
        open={templateDialogOpen}
        onClose={handleCloseTemplateDialog}
        currentTemplate={currentTemplate}
        templateForm={templateForm}
        setTemplateForm={setTemplateForm}
        onSave={handleSaveTemplate}
      />

      <ViewTemplateDialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        template={viewTemplate}
      />

      <SendMessageDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        sendForm={sendForm}
        setSendForm={setSendForm}
        templates={templates}
        selectedUsers={selectedUsers}
        setSelectedUsers={setSelectedUsers}
        userSearchInput={userSearchInput}
        setUserSearchInput={setUserSearchInput}
        userSearchResults={userSearchResults}
        onTemplateSelect={handleTemplateSelect}
        onSend={handleSendMessage}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
